"use client";

import React from "react";

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, startIdx, endIdx }) => {
  if (totalPages <= 1) return null;

  const [jumpValue, setJumpValue] = React.useState("");

  const handleJump = (e) => {
    e.preventDefault();
    const num = parseInt(jumpValue, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num);
      setJumpValue("");
    }
  };

  // Helper to show a limited set of page squares
  const getPageSquares = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages, start + 1);
    
    if (currentPage === 1) end = Math.min(totalPages, 2);
    if (currentPage === totalPages) start = Math.max(1, totalPages - 1);

    for (let i = start; i <= end; i++) {
       pages.push(i);
    }
    return pages;
  };

  return (
    <div className="pagination-master-wrapper font-montserrat">
      {/* 1. Pagination Row (Top) */}
      <div className="pagination-row-top">
        <button
          className="nav-sq-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <span className="arrow">←</span>
        </button>

        {getPageSquares().map((num) => (
          <button
            key={num}
            className={`nav-sq-btn ${currentPage === num ? "active" : ""}`}
            onClick={() => onPageChange(num)}
          >
            {num}
          </button>
        ))}

        <button
          className="nav-sq-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <span className="arrow">→</span>
        </button>
      </div>

      {/* 2. Jump to Page Row (Middle) */}
      <div className="pagination-row-middle">
        <div className="jump-label">
          Jump to<br />page
        </div>
        <form onSubmit={handleJump} className="jump-area">
          <input
            type="number"
            min="1"
            max={totalPages}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            placeholder="—"
            className="jump-pill-input"
          />
          <button type="submit" className="go-btn">Go</button>
        </form>
      </div>

      {/* 3. Status Text (Bottom) */}
      <div className="pagination-row-bottom">
        Showing {startIdx}–{endIdx} of {totalItems} surgeons
      </div>

      <style jsx>{`
        .pagination-master-wrapper {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 25px;
          padding: 40px 0;
          background: transparent;
        }

        /* Row 1: Square Buttons */
        .pagination-row-top {
          display: flex;
          gap: 8px;
          justify-content: center;
        }
        .nav-sq-btn {
          width: 44px;
          height: 44px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          color: #0f172a;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .nav-sq-btn:hover:not(:disabled):not(.active) {
          background: #f1f5f9;
          border-color: #94a3b8;
          color: #0f172a;
        }
        .nav-sq-btn.active {
          background: #1254a0;
          border-color: #1254a0;
          color: #ffffff;
          box-shadow: 0 4px 6px -1px rgba(18, 84, 160, 0.3);
        }
        .nav-sq-btn.active:hover {
          background: #0e4492;
          border-color: #0e4492;
          color: #ffffff;
        }
        .nav-sq-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: #f8fafc;
          color: #94a3b8;
          border-color: #e2e8f0;
        }
        .arrow {
          font-size: 1.2rem;
          line-height: 1;
        }

        /* Row 2: Jump to Area */
        .pagination-row-middle {
          display: flex;
          align-items: center;
          gap: 15px;
          width: 100%;
          max-width: 380px; 
        }
        .jump-label {
          color: #64748b;
          font-size: 13px;
          line-height: 1.25;
          text-align: left;
          min-width: 55px;
          font-weight: 500;
        }
        .jump-area {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .jump-pill-input {
          flex: 1;
          height: 42px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 21px; 
          color: #1e293b;
          padding: 0 15px;
          font-size: 1rem;
          text-align: center;
          transition: all 0.3s;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
        }
        .jump-pill-input:focus {
          outline: none;
          border-color: #1254a0;
          box-shadow: 0 0 0 3px rgba(18, 84, 160, 0.1);
        }
        .go-btn {
          height: 42px;
          padding: 0 20px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 21px;
          color: #1e293b;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }
        .go-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        /* Row 3: Status Text */
        .pagination-row-bottom {
          color: #64748b;
          font-size: 14px;
          text-align: center;
          margin-top: 5px;
          letter-spacing: 0.3px;
        }

        /* Mobile Adjustments */
        @media (max-width: 500px) {
          .pagination-master-wrapper {
            padding: 20px 10px;
            gap: 15px;
            box-sizing: border-box;
          }
          .pagination-row-top {
            flex-wrap: nowrap;
            gap: 8px;
          }
          .nav-sq-btn {
            width: 40px;
            height: 40px;
            min-width: 40px;
            padding: 0;
            font-size: 0.9rem;
          }
          .pagination-row-middle {
            flex-direction: row;
            gap: 10px;
            padding: 0 10px;
          }
          .jump-label {
            display: block;
            font-size: 11px;
            min-width: 45px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Pagination;
